export type DateOptions = Intl.DateTimeFormatOptions;

const DEFAULT_DATE: DateOptions = { dateStyle: 'medium' };
const DEFAULT_TIME: DateOptions = { timeStyle: 'short' };
const DEFAULT_DT: DateOptions = { dateStyle: 'medium', timeStyle: 'short' };

export function formatDate(value: Date, locale: string, opts: DateOptions = {}): string {
  return new Intl.DateTimeFormat(locale, { ...DEFAULT_DATE, ...opts }).format(value);
}

export function formatTime(value: Date, locale: string, opts: DateOptions = {}): string {
  return new Intl.DateTimeFormat(locale, { ...DEFAULT_TIME, ...opts }).format(value);
}

export function formatDateTime(value: Date, locale: string, opts: DateOptions = {}): string {
  return new Intl.DateTimeFormat(locale, { ...DEFAULT_DT, ...opts }).format(value);
}

const REL_UNITS: ReadonlyArray<[Intl.RelativeTimeFormatUnit, number]> = [
  ['year', 365 * 24 * 60 * 60 * 1000],
  ['month', 30 * 24 * 60 * 60 * 1000],
  ['week', 7 * 24 * 60 * 60 * 1000],
  ['day', 24 * 60 * 60 * 1000],
  ['hour', 60 * 60 * 1000],
  ['minute', 60 * 1000],
  ['second', 1000],
];

export interface RelativeOptions extends Intl.RelativeTimeFormatOptions {
  readonly now?: number;
}

export function formatRelativeTime(
  value: Date,
  locale: string,
  opts: RelativeOptions = {},
): string {
  const now = opts.now ?? Date.now();
  const deltaMs = value.getTime() - now;
  const abs = Math.abs(deltaMs);
  const match = REL_UNITS.find(([, d]) => abs >= d);
  const [unit, divisor] =
    match ?? (REL_UNITS[REL_UNITS.length - 1] as [Intl.RelativeTimeFormatUnit, number]);
  const rounded = Math.round(deltaMs / divisor);

  // Build RTF options conditionally to satisfy exactOptionalPropertyTypes:
  // passing `style: undefined` or `numeric: undefined` would be a type error.
  const rtfOpts: Intl.RelativeTimeFormatOptions = {
    numeric: opts.numeric ?? 'auto',
    ...(opts.style !== undefined ? { style: opts.style } : {}),
  };

  const rtf = new Intl.RelativeTimeFormat(locale, rtfOpts);
  return rtf.format(rounded, unit);
}
